import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import AuthInitializer from "components/common/AuthInitializer";
import DataInitializer from "components/common/DataInitializer";
import AdminLayout from "components/templates/AdminLayout";
import CommonUI, { ToastManager } from "components/organisms/CommonUI";
import Dashboard from "pages/Dashboard";
import Login from "pages/Login";
import KakaoCallback from "pages/Login/KakaoCallback";
import LandingPage from "pages/LandingPage";
import Patients from "pages/Admin/Patients";
import PatientDetailPage from "pages/Patients/PatientDetailPage";
import PatientLayout from "pages/Patients";
import AIReviewDetailPage from "pages/Reviews/AIReviewDetailPage";
import AIReviewPrintPage from "pages/AIReviews/AIReviewPrintPage";
import Medications from "pages/Admin/Medications";
import HealthFunctionalFoods from "pages/Admin/HealthFunctionalFoods";
import Prompts from "pages/Admin/Prompts";
import Users from "pages/Admin/Users";
import PremiumLicenses from "pages/Admin/PremiumLicenses";
import PharmacyAccess from "pages/Admin/PharmacyAccess";
import Subscriptions from "pages/Admin/Subscriptions";
import AdminAIReviews from "pages/Admin/AIReviews";
import AIReviewStatistics from "pages/Admin/AIReviewStatistics";
import AdminAIReviewPopup from "pages/Admin/AIReviews/AdminAIReviewPopup";
import AdminAIReviewComparePopup from "pages/Admin/AIReviews/AdminAIReviewComparePopup";
import AdminGroundTruths from "pages/Admin/AIReviewTest/GroundTruths";
import AdminAIReviewTestResults from "pages/Admin/AIReviewTest/Results";
import AdminAIReviewTestAnalysis from "pages/Admin/AIReviewTest/Analysis";
import AdminDrugIdentificationsPage from "pages/Admin/DrugIdentifications";
import DrugIdentificationDetailPopup from "pages/Admin/DrugIdentifications/DrugIdentificationDetailPopup";
import AdminFeedbacksPage from "pages/Admin/Feedbacks";
import AdminConsultationRecordsPage from "pages/Admin/ConsultationRecords";
import AdminConsultationRecordPopup from "pages/Admin/ConsultationRecords/AdminConsultationRecordPopup";
import AdminDoseRules from "pages/Admin/DoseRules";
import AdminDoseRuleDetail from "pages/Admin/DoseRules/Detail";
import DoseReviewLogs from "pages/Admin/DoseReviewLogs";
import DoseReviewLogDetailRoute from "pages/Admin/DoseReviewLogs/Detail";
import AdminPartners from "pages/Admin/Partners";
import AdminPartnerDetail from "pages/Admin/Partners/Detail";
import Unauthorized from "pages/Unauthorized";
import TestPage from "pages/Test";
import MedicationReviewPage from "pages/Test/MedicationReview";
import MyPage from "pages/MyPage";
import Agreements from "pages/Agreements";
import YmydSignup from "pages/YmydSignup";
import DesktopAuth from "pages/DesktopAuth";
import DesktopDownload from "pages/DesktopDownload";
import CaringNoteEmbedPage from "pages/CaringNote/CaringNoteEmbedPage";
import CaringNoteReportPage from "pages/CaringNote/CaringNoteReportPage";
import { ReactChannelIO } from "react-channel-plugin";
import { AppDispatch, RootState } from "store";
import { useDispatch, useSelector } from "react-redux";
import { setUserProperty } from "utils/AmplitudeUtil";
import { useChannelIOApi } from "react-channel-plugin";
import { log, LogLevel } from "utils/LogUtil";
import { isMobileBrowser, isTablet } from "utils/DeviceUtil";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import { COLOR_SYSTEM } from "components/design/design-system";
import NhisConsentRouter from "pages/NHIS";
import { requestAndRegisterFcmToken, setupForegroundListener } from "services/fcmService";
import { handleFcmMessage } from "services/fcmMessageHandler";

const App: React.FC = () => {
  return (
    <BrowserRouter>
      <Routes>
        {/* NHIS 환자 페이지 (인증 불필요, 모바일 최적화) */}
        {/* AuthInitializer, DataInitializer, ReactChannelIO, 모바일 경고 모두 미적용 */}
        <Route path="/nhis/*" element={<NhisConsentRouter />} />

        {/* 복약지도문 자문 첨삭 비교 (임시 공개 — 인증 불필요) */}
        <Route path="/p/a3f8b2c1" element={<MedicationReviewPage />} />

        {/* 데스크톱 인증 핸드오프 (비로그인 접근 가능 — 진입·완료를 감싸는 얇은 레이어) */}
        {/* AuthInitializer/인터셉터 리다이렉트 밖에 둔다: AuthenticatedApp 안에 두면 비로그인 */}
        {/* 초기 진입에서 마운트 전에 /login으로 튕겨 cb·state 저장 기회를 잃는다 (spec §6) */}
        <Route path="/desktop-auth" element={<DesktopAuth />} />

        {/* PhAI 약국 설치 파일 다운로드 (완전 공개 — 직링크 전용, 사이트 내 링크 없음) */}
        {/* catch-all `/*` 앞에 둔다: 안에 두면 비로그인 약사가 /login으로 튕긴다 (spec §변경 사항) */}
        <Route path="/download" element={<DesktopDownload />} />

        {/* 케어링노트 파트너 열람 2종 (인증 불필요 — URL 토큰이 유일한 인증) */}
        {/* AuthenticatedApp 밖 · /* 앞에 둔다: 안에 두면 로그인 화면으로 리다이렉트된다 (계획 §1-1) */}
        <Route path="/caringnote/embed/:token" element={<CaringNoteEmbedPage />} />
        <Route path="/caringnote/report/:token" element={<CaringNoteReportPage />} />

        {/* 기존 약사 앱 (인증 필요) */}
        <Route path="/*" element={<AuthenticatedApp />} />
      </Routes>
    </BrowserRouter>
  );
};

// 기존 앱 로직을 별도 컴포넌트로 분리
const AuthenticatedApp: React.FC = () => {
  const user = useSelector((state: RootState) => state.user.user);

  return (
    <ReactChannelIO
      pluginKey={"6fb9029c-e5d0-4aa8-8bed-6dd8a9db70fb"}
      language="ko"
      memberId={user && user.id ? user.id : undefined}
      autoBoot
    >
      <AppInsider />
    </ReactChannelIO>
  );
};

const AppInsider: React.FC = () => {
  const { updateUser, hideChannelButton } = useChannelIOApi();
  const user = useSelector((state: RootState) => state.user.user);
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();

  useEffect(() => {
    // 모바일 브라우저 감지 및 Toast 표시
    // CommonUI 컴포넌트가 마운트된 후에 Toast를 표시하기 위해 약간의 지연 추가
    const timer = setTimeout(() => {
      // 스마트폰에서만 경고 표시 (태블릿은 제외)
      if (isMobileBrowser() && !isTablet()) {
        ToastManager.show({
          content: (
            <div
              style={{
                textAlign: "center",
                padding: "8px 0",
              }}
            >
              <div style={{ marginBottom: "16px" }}>
                <Icon 
                  name="Exclamation Mark Polygon" 
                  size={32} 
                  color={COLOR_SYSTEM.get("Orange")![500]} 
                />
              </div>
              <Text
                element="div"
                color={COLOR_SYSTEM.get("Gray")![600]}
                style={{ marginBottom: "12px" }}
              >
                휴대폰 화면으로는 사용이 어렵습니다.<br />
              </Text>
              <Text 
                element="div" 
                textType="H4" 
                color={COLOR_SYSTEM.get("Gray")![600]} 
                style={{ marginBottom: "8px" }}
              >
                <Text element="span" textType="H4" color={COLOR_SYSTEM.get("Blue")![500]}>
                  컴퓨터나 태블릿
                </Text>
                으로 접속해주세요.
              </Text>
            </div>
          ),
          duration: 60000, // 1분 (60초)
          position: "top",
          style: {
            backgroundColor: COLOR_SYSTEM.get("Yellow")![50],
            border: `2px solid ${COLOR_SYSTEM.get("Yellow")![500]}`,
            color: COLOR_SYSTEM.get("Gray")![600],
            boxShadow: "0 8px 24px rgba(39, 54, 72, 0.12)",
            padding: "20px 48px 20px 24px",
            minWidth: "350px",
            borderRadius: "8px",
          },
        });
      }
    }, 100); // 100ms 지연

    return () => clearTimeout(timer);
  }, []); // 컴포넌트 마운트 시 한 번만 실행

  useEffect(() => {
    if (user && user.id) {
      log(LogLevel.VERBOSE, "AppInsider: useEffect", user);
      setUserProperty(user);
      hideChannelButton();
      updateUser({
        profile: {
          name: user.name,
          email: user.email,
          phmobileNumberone: user.phone,
          pharmacy: user.company,
          type: user.roles.includes("admin") ? "관리자" : "PHAI사용자",
          referrer: "PHAI",
        },
      });
    }
  }, [user]);

  // FCM 토큰 등록
  useEffect(() => {
    if (user?.id) {
      requestAndRegisterFcmToken().catch((err) =>
        log(LogLevel.ERROR, "FCM 토큰 등록 실패:", err)
      );
    }
  }, [user?.id]);

  // FCM 포그라운드 메시지 리스너
  useEffect(() => {
    const unsubscribe = setupForegroundListener((payload) => {
      const data = payload.data;
      if (data?.type) {
        handleFcmMessage(data, navigate, dispatch, payload.notification);
      } else if (payload.notification) {
        // 핸들러가 처리하지 않은 일반 알림만 기본 토스트 표시
        ToastManager.show({
          content: payload.notification.body || "새 알림이 있습니다.",
          duration: 3000,
        });
      }
    });
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [navigate]);

  return (
    <AuthInitializer>
      <DataInitializer>
        <Routes>
          {/* 랜딩 페이지 */}
          <Route path="/" element={<LandingPage />} />

          {/* 로그인 */}
          <Route path="/login" element={<Login />} />

          {/* 카카오 OAuth 콜백 (회원가입 흐름) */}
          <Route path="/auth/kakao/callback" element={<KakaoCallback />} />

          {/* 회원가입 */}
          <Route path="/signup/ymyd" element={<YmydSignup />} />

          {/* 약관 동의 관련 */}
          <Route path="/agreements" element={<Agreements />} />

          {/* 접근 권한 없음 안내 (AdminRoute/withPageGuard가 여기로 리다이렉트) */}
          <Route path="/unauthorized" element={<Unauthorized />} />

          {/* 관리자 영역 */}
          <Route path="/admin" element={<AdminLayout />}>
            <Route path="medications" element={<Medications />} />
            <Route path="health-functional-foods" element={<HealthFunctionalFoods />} />
            <Route path="prompts" element={<Prompts />} />
            <Route path="users" element={<Users />} />
            <Route path="premium-licenses" element={<PremiumLicenses />} />
            <Route path="pharmacy-access" element={<PharmacyAccess />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="ai-reviews" element={<AdminAIReviews />} />
            <Route path="feedbacks" element={<AdminFeedbacksPage />} />
            <Route path="drug-identifications" element={<AdminDrugIdentificationsPage />} />
            <Route path="ai-review-test/ground-truths" element={<AdminGroundTruths />} />
            <Route path="ai-review-test/results" element={<AdminAIReviewTestResults />} />
            <Route path="ai-review-test/analysis" element={<AdminAIReviewTestAnalysis />} />
            <Route path="statistics/ai-reviews" element={<AIReviewStatistics />} />
            <Route path="consultation-records" element={<AdminConsultationRecordsPage />} />
            <Route path="dose-rules" element={<AdminDoseRules />} />
            <Route path="dose-rules/:ruleId" element={<AdminDoseRuleDetail />} />
            <Route path="dose-review-logs" element={<DoseReviewLogs />} />
            <Route path="dose-review-logs/:logId" element={<DoseReviewLogDetailRoute />} />
            <Route path="partners" element={<AdminPartners />} />
            <Route path="partners/:id" element={<AdminPartnerDetail />} />
            {/* 필요시 추가 관리자 하위 라우트 */}
          </Route>

          {/* AI Review 팝업 라우트 (레이아웃 없음) */}
          <Route path="/admin/ai-reviews/:reviewId/popup" element={<AdminAIReviewPopup />} />
          <Route
            path="/admin/ai-reviews/:reviewId/popup/evaluation/:evaluationId"
            element={<AdminAIReviewPopup />}
          />
          <Route path="/admin/ai-reviews/compare/popup" element={<AdminAIReviewComparePopup />} />

          {/* 의약품 식별 팝업 라우트 (레이아웃 없음) */}
          <Route path="/admin/drug-identifications/:id/popup" element={<DrugIdentificationDetailPopup />} />

          {/* 상담 녹음 기록 팝업 라우트 (레이아웃 없음) */}
          <Route path="/admin/consultation-records/:id/popup" element={<AdminConsultationRecordPopup />} />

          {/* 대시보드 */}
          <Route path="/dashboard" element={<Dashboard />} />

          {/* 환자 영역 (Sidebar 고정) */}
          <Route path="/patients" element={<PatientLayout />}>
            <Route index element={<Patients />} />
            <Route path=":patientId" element={<PatientDetailPage />} />
            <Route path=":patientId/reviews/:reviewId" element={<AIReviewDetailPage />} />
          </Route>

          {/* AI Review 인쇄 페이지 (레이아웃 없음) */}
          <Route path="/ai-reviews/:id/print" element={<AIReviewPrintPage />} />

          {/* 마이페이지 */}
          <Route path="/mypage/*" element={<MyPage />} />

          {/* 테스트 메뉴 (개발 중일 때만 표시) */}
          {process.env.NODE_ENV === "development" && <Route path="test" element={<TestPage />} />}
          {process.env.NODE_ENV === "development" && <Route path="test/medication-review" element={<MedicationReviewPage />} />}
          {/* 기타 라우트 */}
          {/* <Route path="..." element={<... />} /> */}
        </Routes>
        <CommonUI />
      </DataInitializer>
    </AuthInitializer>
  );
};

export default App;
